import { MCDANIEL_NAME } from "constants/settings.constants";

import { useProjectContext, useProjectDispatch } from "../../../../../projects/context";
import {
  useSelectedProject,
  useSelectedProjectPermissions
} from "../../../../../projects/hooks";
import { useDeleteShapefileList } from "../../../../../shapefiles/hooks/delete-shapefile-list/useDeleteShapefileList";
import { useProjectLayerTree } from "../../../../hooks";
import { filterShapefileKeys } from "../../../../utils/filterShapefileKeys";
import { DisplayMode } from "../../../layers-tab/constants/layersTab.constants";
import { ACTION_TEXT } from "../constants/actionText.constants";

interface UseSelectItemsActionProps {
  isDisabled?: boolean;
  setPopoverOpen?: (isOpen: boolean) => void;
  onContextMenuClose?: () => void;
  allowEmptySelection?: boolean; // For LayersTab context - allows manage mode even with no selection
}

/**
 * Hook for managing select items action functionality.
 *
 * NOTE: This hook is currently only implemented for the Project tree.
 * If desired for Organization/System trees, the action will need to be refactored
 * in ShapefileActions/ShapefileFolderActions to be passed in as a prop if there
 * are differences between trees, similar to the rest of the actions.
 */
export function useSelectItemsAction({
  isDisabled,
  setPopoverOpen,
  onContextMenuClose
}: UseSelectItemsActionProps) {
  const projectContext = useProjectContext();
  const projectDispatch = useProjectDispatch();
  const { selectedProject: project } = useSelectedProject();
  const selectedProjectPermissions = useSelectedProjectPermissions();
  const { tree } = useProjectLayerTree();
  const deleteShapefileModal = useDeleteShapefileList();

  // Calculate disabled state based on permissions (selection not required)
  const hasNoPermission = !selectedProjectPermissions?.canEditProjectItems;
  const hasNoProject = !project;
  const isMcDanielProject = project?.projectType === MCDANIEL_NAME;
  const isInManageMode = deleteShapefileModal.manageLayerTree.isManageMode;
  const isInLayerListMode =
    projectContext.displaySettings?.displayMode === DisplayMode.LIST;

  const hasValidationIssues = hasNoPermission || hasNoProject || isMcDanielProject;
  const isDisabledLocal =
    isDisabled || isInLayerListMode || (!isInManageMode && hasValidationIssues);

  const handleSelectItems = (evt?: React.MouseEvent) => {
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }

    if (isInManageMode) {
      deleteShapefileModal.manageLayerTree.handleExitManageMode();
    } else {
      const currentCheckedKeys = projectContext.checkedTreeKeys || [];
      const shapefileKeys = filterShapefileKeys(currentCheckedKeys, tree);

      projectDispatch({
        payload: {
          manageMode: {
            isEnabled: true,
            checkedKeys: shapefileKeys
          }
        }
      });
    }

    if (setPopoverOpen) {
      setPopoverOpen(false);
    }
    if (onContextMenuClose) {
      onContextMenuClose();
    }
  };

  const actionText = isInManageMode
    ? ACTION_TEXT.EXIT_MANAGE_MODE
    : ACTION_TEXT.MANAGE_SELECTED_ITEMS;

  return {
    isDisabled: isDisabledLocal,
    handleSelectItems,
    actionText
  };
}
